import { useState, useEffect, useCallback } from 'react'
import { Newspaper, RefreshCw, ExternalLink, Bookmark, Users } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function TopStoriesPage() {
  const [stories, setStories] = useState<any[]>([])
  const [meta, setMeta] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/dedup/stories', { params: { limit: 40, hours: 48 } })
      setStories(data.stories || [])
      setMeta(data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const bookmark = async (title: string, url: string, source: string) => {
    try { await api.post('/api/content/bookmarks', { article_title: title, article_url: url, article_source: source }) } catch {}
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Newspaper size={20} className="text-blue" /> Top Stories
          </h1>
          <p className="text-[10px] text-text-secondary">
            {meta?.dedup_ratio || 'Loading...'} — same story from multiple sources merged
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !stories.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
        </div>
      )}

      <div className="space-y-3">
        {stories.map((story, i) => (
          <div key={i} className={clsx('card overflow-hidden transition-all',
            story.source_count >= 3 && 'border-l-4 border-l-blue')}>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Source count badge */}
                  <div className="flex items-center gap-2 mb-1.5">
                    {story.source_count > 1 && (
                      <span className="badge-blue text-[9px] flex items-center gap-1">
                        <Users size={9} /> {story.source_count} sources
                      </span>
                    )}
                    <span className="text-[9px] text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">{story.category}</span>
                    <span className="text-[9px] text-text-muted">{relativeTime(story.timestamp)}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-sm font-semibold text-text-primary leading-snug">
                    {story.url ? (
                      <a href={story.url} target="_blank" rel="noopener" className="hover:underline">{story.title}</a>
                    ) : story.title}
                  </h3>

                  {/* Summary */}
                  {story.summary && (
                    <p className="text-[11px] text-text-secondary mt-1.5 leading-relaxed line-clamp-2">{story.summary}</p>
                  )}

                  {/* Sources list */}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {story.sources.map((s: string) => (
                      <span key={s} className="text-[8px] text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 shrink-0">
                  <button type="button" onClick={() => bookmark(story.title, story.url, story.primary_source)}
                    className="p-1.5 rounded hover:bg-surface-hover text-text-muted hover:text-amber transition-colors" title="Bookmark">
                    <Bookmark size={13} />
                  </button>
                  {story.url && (
                    <a href={story.url} target="_blank" rel="noopener"
                      className="p-1.5 rounded hover:bg-surface-hover text-text-muted hover:text-blue-light transition-colors" title="Open">
                      <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </div>

              {/* Expandable: show all articles from all sources */}
              {story.article_count > 1 && (
                <button type="button" onClick={() => setExpanded(expanded === story.title ? null : story.title)}
                  className="text-[9px] text-blue-light hover:underline mt-2">
                  {expanded === story.title ? 'Hide' : 'Show'} all {story.article_count} articles
                </button>
              )}
            </div>

            {expanded === story.title && story.articles?.length > 1 && (
              <div className="border-t border-border bg-surface-hover/30 px-4 py-2">
                {story.articles.map((a: any, j: number) => (
                  <div key={j} className="flex items-center gap-2 py-1 text-[10px]">
                    <span className="text-text-muted bg-surface-card px-1.5 py-0.5 rounded shrink-0">{a.source}</span>
                    <p className="text-text-secondary truncate flex-1">
                      {a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a> : a.title}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
